import React, { useMemo } from 'react';
import { Button, Dropdown, Typography } from 'antd';
import type { MenuProps } from 'antd';
import { SettingOutlined, EyeOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { usePermissions } from '../../features/permissions/PermissionsContext';
import { confirmDelete } from './confirmDelete';

const { Text } = Typography;

export interface CogRowActionsProps {
  rowNumber?: number;
  onView?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  viewLabel?: string;
  editLabel?: string;
  deleteLabel?: string;
  editPermission?: string;
  deletePermission?: string;
  deleteDisabled?: boolean;
  deleteConfirm?: { title: string; content: string };
  extraItems?: MenuProps['items'];
}

const CogRowActions: React.FC<CogRowActionsProps> = ({
  rowNumber,
  onView,
  onEdit,
  onDelete,
  viewLabel = 'View details',
  editLabel = 'Edit',
  deleteLabel = 'Delete',
  editPermission,
  deletePermission,
  deleteDisabled = false,
  deleteConfirm,
  extraItems,
}) => {
  const { hasPermission } = usePermissions();

  const showEdit = onEdit !== undefined && (editPermission ? hasPermission(editPermission) : true);
  const showDelete =
    onDelete !== undefined && (deletePermission ? hasPermission(deletePermission) : true);

  const items = useMemo((): MenuProps['items'] => {
    const built: MenuProps['items'] = [];

    if (rowNumber !== undefined) {
      built.push({
        key: 'row-number',
        label: (
          <Text strong style={{ fontSize: 13 }}>
            #{rowNumber}
          </Text>
        ),
        disabled: true,
      });
      built.push({ type: 'divider' });
    }

    if (onView !== undefined) {
      built.push({
        key: 'view',
        label: viewLabel,
        icon: <EyeOutlined />,
        onClick: onView,
      });
    }

    if (showEdit && onEdit !== undefined) {
      built.push({
        key: 'edit',
        label: editLabel,
        icon: <EditOutlined />,
        onClick: onEdit,
      });
    }

    if (extraItems !== undefined && extraItems.length > 0) {
      built.push({ type: 'divider' });
      built.push(...extraItems);
    }

    if (showDelete && onDelete !== undefined) {
      built.push({ type: 'divider' });
      built.push({
        key: 'delete',
        label: deleteLabel,
        icon: <DeleteOutlined />,
        danger: true,
        disabled: deleteDisabled,
        onClick: deleteDisabled
          ? undefined
          : () => {
              if (deleteConfirm) {
                confirmDelete({
                  title: deleteConfirm.title,
                  content: deleteConfirm.content,
                  onOk: onDelete,
                });
              } else {
                onDelete();
              }
            },
      });
    }

    return built;
  }, [
    rowNumber,
    onView,
    onEdit,
    showEdit,
    viewLabel,
    editLabel,
    extraItems,
    showDelete,
    onDelete,
    deleteLabel,
    deleteDisabled,
    deleteConfirm,
  ]);

  const interactiveCount =
    (onView !== undefined ? 1 : 0) +
    (showEdit ? 1 : 0) +
    (showDelete ? 1 : 0) +
    (extraItems?.length ?? 0);

  if (interactiveCount === 0) {
    return (
      <Button
        type="text"
        size="small"
        icon={<SettingOutlined />}
        aria-label="Actions"
        disabled
      />
    );
  }

  if (
    interactiveCount === 1 &&
    onView !== undefined &&
    !showEdit &&
    !showDelete &&
    (extraItems?.length ?? 0) === 0
  ) {
    return (
      <Button
        type="text"
        size="small"
        icon={<SettingOutlined />}
        aria-label="Actions"
        onClick={(e) => {
          e.stopPropagation();
          onView();
        }}
      />
    );
  }

  return (
    <Dropdown
      menu={{ items, onClick: ({ domEvent }) => domEvent.stopPropagation() }}
      trigger={['click']}
      placement="bottomRight"
    >
      <Button
        type="text"
        size="small"
        icon={<SettingOutlined />}
        aria-label="Actions"
        onClick={(e) => e.stopPropagation()}
      />
    </Dropdown>
  );
};

export default CogRowActions;
